import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MapPin, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SITE } from '../seo/siteMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = ({ initialService = '' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: initialService,
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Client-side validation
    if (!formData.name.trim()) {
      toast.error('Please enter your name', { duration: 4000, position: 'top-right' });
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Please enter your email address', { duration: 4000, position: 'top-right' });
      return;
    }
    if (!formData.service) {
      toast.error('Please select a service type', { duration: 4000, position: 'top-right' });
      return;
    }
    if (!formData.message.trim()) {
      toast.error('Please enter a message', { duration: 4000, position: 'top-right' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, formData);

      if (response.data.status === 'success') {
        toast.success(response.data.message || 'Thank you! We\'ll get back to you shortly.', {
          duration: 5000,
          position: 'top-right',
        });
        // A successful CRM submission is a lead; clicks and failed requests are not.
        // A Google Tag Manager trigger can listen for this event after consent is granted.
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'harp_lead_submitted', service: formData.service });
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: initialService,
          message: '',
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);

      // Safely parse FastAPI error responses. `detail` may be a string OR an array of
      // Pydantic validation errors (422). Handle both without crashing the UI.
      const detail = error?.response?.data?.detail;
      let errorMessage = 'Something went wrong. Please try again.';

      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMessage = detail
          .map((d) => {
            if (typeof d === 'string') return d;
            const field = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
            const msg = d?.msg || 'Invalid value';
            return field ? `${field}: ${msg}` : msg;
          })
          .join(' \u2022 ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || JSON.stringify(detail);
      }

      toast.error(errorMessage, {
        duration: 5000,
        position: 'top-right',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Get in Touch
          </h2>
          <p className="text-xl text-gray-400">
            Ready to discuss your engineering needs? Contact us today.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white">Send Us a Message</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                        Name *
                      </label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        className="bg-gray-800 border-gray-700 text-white focus:border-amber-600 focus:ring-amber-600"
                        placeholder="Your name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                        Email *
                      </label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="bg-gray-800 border-gray-700 text-white focus:border-amber-600 focus:ring-amber-600"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                        Phone
                      </label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        className="bg-gray-800 border-gray-700 text-white focus:border-amber-600 focus:ring-amber-600"
                        placeholder="Your phone number"
                      />
                    </div>
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-gray-300 mb-2">
                        Service Type *
                      </label>
                      <Select value={formData.service} onValueChange={handleServiceChange} required>
                        <SelectTrigger className="bg-gray-800 border-gray-700 text-white focus:border-amber-600 focus:ring-amber-600">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-gray-800 border-gray-700">
                          <SelectItem value="handyman" className="text-white hover:bg-gray-700">Handyman</SelectItem>
                          <SelectItem value="air-conditioning" className="text-white hover:bg-gray-700">Air Conditioning</SelectItem>
                          <SelectItem value="refrigeration" className="text-white hover:bg-gray-700">Refrigeration</SelectItem>
                          <SelectItem value="plumbing" className="text-white hover:bg-gray-700">Plumbing</SelectItem>
                          <SelectItem value="emergency" className="text-white hover:bg-gray-700">Emergency Service</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                      Message *
                    </label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      rows={6}
                      className="bg-gray-800 border-gray-700 text-white focus:border-amber-600 focus:ring-amber-600"
                      placeholder="Tell us about your requirements..."
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white py-6 text-lg rounded-md transition-all duration-300 hover:shadow-xl hover:shadow-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Sending...
                      </span>
                    ) : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            <Card className="bg-gray-900/50 border-gray-800">
              <CardContent className="p-6 space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-amber-600/20 rounded-lg">
                    <Phone className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Phone</h3>
                    <a 
                      href="tel:+443301221094" 
                      className="text-gray-400 hover:text-amber-500 transition-colors text-lg font-medium"
                    >
                      +44 330 122 1094
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-amber-600/20 rounded-lg">
                    <Mail className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-1">Email</h3>
                    <a href="mailto:info@harp-solutions.co.uk" className="text-gray-400 hover:text-amber-500 transition-colors">
                      info@harp-solutions.co.uk
                    </a>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-amber-600/20 rounded-lg">
                    <MapPin className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white mb-2">Registered Office</h3>
                    <a
                      href={SITE.mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-gray-400 hover:text-amber-500 transition-colors not-italic leading-relaxed"
                      data-testid="contact-address-link"
                    >
                      <span className="block">H.A.R.P Solutions LTD</span>
                      <span className="block">Suite RA01</span>
                      <span className="block">195-197 Wood Street</span>
                      <span className="block">London</span>
                      <span className="block">E17 3NU</span>
                    </a>
                    <p className="text-gray-500 text-sm mt-3 leading-snug">
                      Serving London, Greater London, Essex, Kent &amp; Surrounding Areas
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-900/30 to-amber-950/20 border-amber-600/30">
              <CardContent className="p-6">
                <h3 className="font-bold text-white text-lg mb-2">24/7 Emergency Service</h3>
                <p className="text-gray-300 text-sm mb-4">
                  Need immediate assistance? We&apos;re always ready to respond to emergency calls.
                </p>
                <a
                  href="tel:+443301221094"
                  className="inline-flex items-center justify-center gap-2 w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-4 rounded-md transition-all duration-300 hover:shadow-lg"
                >
                  <Phone className="w-5 h-5" />
                  Call Now: +44 330 122 1094
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
